import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { LangProvider } from "./context/LangContext";
import { AuthProvider } from "./context/AuthContext";
import { PropertyProvider } from "./context/PropertyContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminRoute from "./components/AdminRoute";
import PaywallGate from "./components/PaywallGate";
import AppShell from "./components/AppShell";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import CalendarPage from "./pages/CalendarPage";
import Finances from "./pages/Finances";
import Reports from "./pages/Reports";
import PropertyPage from "./pages/PropertyPage";
import PropertyDetail from "./pages/PropertyDetail";
import Properties from "./pages/Properties";
import Bookings from "./pages/Bookings";
import Subscribe from "./pages/Subscribe";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminApprovals from "./pages/admin/AdminApprovals";
import AdminActivations from "./pages/admin/AdminActivations";
import AdminPasswordResets from "./pages/admin/AdminPasswordResets";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminFeedback from "./pages/admin/AdminFeedback";
import AdminAdmins from "./pages/admin/AdminAdmins";
import AdminPayments from "./pages/admin/AdminPayments";
import Feedback from "./pages/Feedback";

const Guarded = ({ children }) => (
  <ProtectedRoute>
    <PropertyProvider>
      <PaywallGate>
        <AppShell>{children}</AppShell>
      </PaywallGate>
    </PropertyProvider>
  </ProtectedRoute>
);

const PayGuarded = ({ children }) => (
  <ProtectedRoute>
    <PropertyProvider>{children}</PropertyProvider>
  </ProtectedRoute>
);

const AdminGuarded = ({ children }) => (
  <AdminRoute>
    <AdminLayout>{children}</AdminLayout>
  </AdminRoute>
);

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route path="/pay" element={<PayGuarded><Subscribe /></PayGuarded>} />
      <Route path="/payment/success" element={<PayGuarded><PaymentSuccess /></PayGuarded>} />
      <Route path="/payment/cancel" element={<PayGuarded><PaymentCancel /></PayGuarded>} />

      <Route path="/app" element={<Guarded><Dashboard /></Guarded>} />
      <Route path="/app/calendar" element={<Guarded><CalendarPage /></Guarded>} />
      <Route path="/app/bookings" element={<Guarded><Bookings /></Guarded>} />
      <Route path="/app/finances" element={<Guarded><Finances /></Guarded>} />
      <Route path="/app/reports" element={<Guarded><Reports /></Guarded>} />
      <Route path="/app/properties" element={<Guarded><Properties /></Guarded>} />
      <Route path="/app/property/:id" element={<Guarded><PropertyDetail /></Guarded>} />
      <Route path="/app/property/:id/edit" element={<Guarded><PropertyPage /></Guarded>} />
      <Route path="/app/feedback" element={<Guarded><Feedback /></Guarded>} />

      <Route path="/admin" element={<AdminGuarded><AdminDashboard /></AdminGuarded>} />
      <Route path="/admin/users" element={<AdminGuarded><AdminUsers /></AdminGuarded>} />
      <Route path="/admin/approvals" element={<AdminGuarded><AdminApprovals /></AdminGuarded>} />
      <Route path="/admin/activations" element={<AdminGuarded><AdminActivations /></AdminGuarded>} />
      <Route path="/admin/password-resets" element={<AdminGuarded><AdminPasswordResets /></AdminGuarded>} />
      <Route path="/admin/settings" element={<AdminGuarded><AdminSettings /></AdminGuarded>} />
      <Route path="/admin/feedback" element={<AdminGuarded><AdminFeedback /></AdminGuarded>} />
      <Route path="/admin/admins" element={<AdminGuarded><AdminAdmins /></AdminGuarded>} />
      <Route path="/admin/payments" element={<AdminGuarded><AdminPayments /></AdminGuarded>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <CurrencyProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </AuthProvider>
      </CurrencyProvider>
    </LangProvider>
  );
}
